import { createContext, FC, ReactNode, useCallback, useContext, useMemo, useRef } from 'react';
import { imageQualityCheck, ImageQualityCheckResult } from '../utils/imageQualityCheck/imageQualityCheck';

type QualityCheckCacheContextType = {
  getOrCheck: (file: File) => Promise<ImageQualityCheckResult>;
  get: (file: File) => ImageQualityCheckResult | undefined;
};

const QualityCheckCacheContext = createContext<QualityCheckCacheContextType | undefined>(undefined);

const keyFor = (file: File) => `${file.size}|${file.lastModified}|${file.type}`;

export const QualityCheckCacheProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const cacheRef = useRef<Map<string, ImageQualityCheckResult>>(new Map());
  const inflightRef = useRef<Map<string, Promise<ImageQualityCheckResult>>>(new Map());

  const get = useCallback((file: File) => cacheRef.current.get(keyFor(file)), []);

  const getOrCheck = useCallback(async (file: File) => {
    const key = keyFor(file);
    const cached = cacheRef.current.get(key);
    if (cached) return cached;
    const inflight = inflightRef.current.get(key);
    if (inflight) return inflight;
    const promise = imageQualityCheck(file)
      .then((result) => {
        cacheRef.current.set(key, result);
        inflightRef.current.delete(key);
        return result;
      })
      .catch((err) => {
        inflightRef.current.delete(key);
        throw err;
      });
    inflightRef.current.set(key, promise);
    return promise;
  }, []);

  const value = useMemo(() => ({ getOrCheck, get }), [getOrCheck, get]);

  return <QualityCheckCacheContext.Provider value={value}>{children}</QualityCheckCacheContext.Provider>;
};

export const useQualityCheckCache = () => {
  const ctx = useContext(QualityCheckCacheContext);
  if (!ctx) throw new Error('useQualityCheckCache must be used within QualityCheckCacheProvider');
  return ctx;
};
